import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection, isDevMode, APP_INITIALIZER } from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { authInterceptor } from '@lumio/shared';
import { profileIncompleteInterceptor } from './infrastructure/interceptors/profile-incomplete.interceptor';
import { provideTransloco } from '@jsverse/transloco';
import { TranslocoHttpLoader } from './transloco-loader';
import { provideRouter, RouteReuseStrategy } from '@angular/router';
import { provideIonicAngular, IonicRouteStrategy } from '@ionic/angular/standalone';
import {
  LucideAngularModule,
  Flame,
  Filter,
  Calendar,
  ChevronDown,
  Home,
  BarChart2,
  Crown,
  User,
  HelpCircle,
  Lock,
  Info,
  ChevronLeft,
  ChevronRight,
  Zap,
  Apple,
  Facebook,
  Search,
  Heart,
  Trophy,
  Gem,
  Star,
  TrendingUp,
  GraduationCap,
  RefreshCw,
  Send,
  Bell,
  Mail,
  Circle,
  Lightbulb,
  Gift,
  PenLine,
  LayoutGrid,
  FileText,
  Tag,
  UserPlus,
  Percent,
  MessageCircle,
  LogOut,
  ArrowUpRight,
  ArrowLeftRight,
  ArrowLeft,
  ArrowRight,
  Sun,
  Moon,
  Eye,
  EyeOff,
  Smartphone,
  MessageSquare,
  Target,
  ShieldCheck,
  Menu,
  X,
  Instagram,
  ChartLine,
  CircleAlert,
  Clock,
  CircleX,
  Twitter,
  Check,
  Sparkles,
  FingerprintPattern,
  LoaderCircle,
} from 'lucide-angular';
import { appRoutes } from './app.routes';
import { ThemeService } from '@lumio/shared';
import { SessionMonitorService } from './infrastructure/session/session-monitor.service';
import { DeepLinkService } from './infrastructure/services/deep-link.service';

const LUMIO_ICONS = {
  Flame,
  Filter,
  Calendar,
  ChevronDown,
  Home,
  BarChart2,
  Crown,
  User,
  HelpCircle,
  Lock,
  Info,
  ChevronLeft,
  ChevronRight,
  Zap,
  Apple,
  Facebook,
  Search,
  Heart,
  Trophy,
  Gem,
  Star,
  TrendingUp,
  GraduationCap,
  RefreshCw,
  Send,
  Bell,
  Mail,
  Circle,
  Lightbulb,
  Gift,
  PenLine,
  LayoutGrid,
  FileText,
  Tag,
  UserPlus,
  Percent,
  MessageCircle,
  LogOut,
  ArrowUpRight,
  ArrowLeftRight,
  ArrowLeft,
  ArrowRight,
  Sun,
  Moon,
  Eye,
  EyeOff,
  Smartphone,
  MessageSquare,
  Target,
  ShieldCheck,
  Menu,
  X,
  Instagram,
  ChartLine,
  CircleAlert,
  Clock,
  CircleX,
  Twitter,
  Check,
  Sparkles,
  FingerprintPattern,
  LoaderCircle,
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideIonicAngular({ mode: 'md' }),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideHttpClient(
      withInterceptors([authInterceptor, profileIncompleteInterceptor])
    ),
    provideRouter(appRoutes),
    provideTransloco({
      config: {
        availableLangs: ['pt', 'en', 'fr'],
        defaultLang: 'pt',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    importProvidersFrom(LucideAngularModule.pick(LUMIO_ICONS)),
    // Ensure theme is applied on full page reloads (F5) before first paint.
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [ThemeService],
      useFactory: (theme: ThemeService) => () => {
        // No-op: injection instantiates ThemeService, which applies `data-theme`
        // based on localStorage / prefers-color-scheme in its constructor effect.
        void theme;
      },
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [SessionMonitorService],
      useFactory: (sessionMonitor: SessionMonitorService) => () => {
        sessionMonitor.startPolling();
        return Promise.resolve();
      },
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [DeepLinkService],
      useFactory: (deepLink: DeepLinkService) => () => deepLink.init(),
    },
  ],
};
